import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  Button, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Card, 
  CardContent, 
  Chip, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableRow, 
  Paper 
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ExpandMore, Refresh, CheckCircle, Warning, Error, Schedule } from '@mui/icons-material';
import HealthyIcon from '../icons/healthy.svg';
import WarningIcon from '../icons/warning.svg';
import apiHealthService, { APIHealth } from '../services/apiHealthService';

const HealthItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1.5),
}));

const HealthLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: '#4A5568',
}));

const HealthStatus = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const StatusText = styled(Typography)<{ status: 'healthy' | 'warning' | 'error' | 'loading' }>(({ theme, status }) => ({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: status === 'healthy' ? '#38A169' : 
         status === 'warning' ? '#D69E2E' : 
         status === 'error' ? '#E53E3E' : '#A0AEC0',
}));

const StatusIcon = styled('img')(({ theme }) => ({
  width: 16,
  height: 16,
}));

const SystemHealth: React.FC = () => {
  const [apiHealth, setApiHealth] = useState<APIHealth[]>([]);
  const [allHealthData, setAllHealthData] = useState<Map<string, APIHealth[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingStates, setLoadingStates] = useState<Set<number>>(new Set());
  const [expanded, setExpanded] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const checkAllAPIs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const allData = await apiHealthService.checkAllHealth();
      setAllHealthData(allData);
      const stagingData = allData.get('staging') || [];
      setApiHealth(stagingData);
      setLastUpdated(new Date());
    } catch (err) {
      setError('Failed to check API health');
      console.error('API health check error:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateSingleAPI = (index: number, updatedApi: APIHealth) => {
    setApiHealth(prev => {
      const newHealth = [...prev];
      newHealth[index] = updatedApi;
      return newHealth;
    });
  };

  const setLoadingForAPI = (index: number, isLoading: boolean) => {
    setLoadingStates(prev => {
      const newSet = new Set(prev);
      if (isLoading) {
        newSet.add(index);
      } else {
        newSet.delete(index);
      }
      return newSet;
    });
  };

  useEffect(() => {
    // Get initial data from cache if available
    const cachedData = apiHealthService.getHealthData('staging');
    if (cachedData && cachedData.length > 0) {
      setApiHealth(cachedData);
      setLoading(false);
    } else {
      checkAllAPIs();
    }
    
    // Subscribe to updates
    const unsubscribe = apiHealthService.addListener((environment, data) => {
      if (environment === 'staging') {
        // Update individual APIs instead of replacing the entire array
        data.forEach((updatedApi, index) => {
          updateSingleAPI(index, updatedApi);
          setLoadingForAPI(index, false);
        });
      }
    });
    
    // Set up periodic health checks every 30 seconds
    const interval = setInterval(() => {
      // Set loading state for all APIs before checking
      apiHealth.forEach((_, index) => {
        setLoadingForAPI(index, true);
      });
      
      checkAllAPIs();
    }, 30000);
    
    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getStatusText = (api: APIHealth, index: number) => {
    if (loadingStates.has(index)) return 'Checking...';
    if (api.status === 'loading') return 'Checking...';
    if (api.status === 'healthy') return `Healthy (${api.responseTime}ms)`;
    if (api.status === 'warning') return `Warning (${api.responseTime}ms)`;
    if (api.status === 'error') return `Error (${api.responseTime}ms)`;
    if (api.status === 'not_configured') return 'Not Configured';
    return 'Unknown';
  };

  const getStatusForDisplay = (api: APIHealth, index: number) => {
    if (loadingStates.has(index)) return 'loading';
    return api.status;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return HealthyIcon;
      case 'warning':
      case 'error':
        return WarningIcon;
      case 'not_configured':
        return WarningIcon; // Use warning icon for not configured
      default:
        return null;
    }
  };

  const getDetailedStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle color="success" />;
      case 'warning':
        return <Warning color="warning" />;
      case 'error':
        return <Error color="error" />;
      case 'loading':
        return <Schedule color="action" />;
      default:
        return <Schedule color="action" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      case 'loading':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatResponseTime = (time?: number) => {
    if (!time) return 'N/A';
    return `${time.toFixed(0)}ms`;
  };

  const formatLastChecked = (timestamp?: string) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1A202C' }}>
          System Health
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Refresh />}
          onClick={checkAllAPIs}
          disabled={loading}
        >
          {loading ? <CircularProgress size={16} /> : 'Refresh'}
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading && apiHealth.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}
      
      {apiHealth.map((api, index) => (
        <HealthItem key={index}>
          <HealthLabel>{api.name}</HealthLabel>
          <HealthStatus>
            {loadingStates.has(index) || api.status === 'loading' ? (
              <CircularProgress size={16} />
            ) : (
              <StatusIcon 
                src={getStatusIcon(getStatusForDisplay(api, index))} 
                alt={getStatusForDisplay(api, index)}
              />
            )}
            <StatusText status={getStatusForDisplay(api, index)}>
              {getStatusText(api, index)}
            </StatusText>
          </HealthStatus>
        </HealthItem>
      ))}

      {/* Expandable Detailed View */}
      <Accordion 
        expanded={expanded} 
        onChange={() => setExpanded(!expanded)}
        sx={{ mt: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="body2" color="primary">
            {expanded ? 'Hide Details' : 'Show Detailed Monitoring'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Information about health checks */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Health Check Information:</strong> This monitors the health of all PropTrak and Aletheia services. 
              Frontend checks verify successful page loads (200 status), while API checks verify endpoint availability. 
              <br /><br />
              <strong>Error Types:</strong>
              <br />• <strong>CORS/Network blocked:</strong> Service is accessible but blocked by browser security - requires Application Gateway routing in production
              <br />• <strong>404/503:</strong> Service endpoint not found or unavailable
              <br />• <strong>Other errors:</strong> Network issues or service problems
            </Typography>
          </Alert>

          {/* Last Updated */}
          {lastUpdated && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Last updated: {lastUpdated.toLocaleString()}
              </Typography>
            </Alert>
          )}

          {/* Environment Tabs */}
          {Array.from(allHealthData.keys()).map((environment) => {
            const apis = allHealthData.get(environment) || [];
            const healthyCount = apis.filter(api => api.status === 'healthy').length;
            const totalCount = apis.length;
            
            return (
              <Accordion key={environment} defaultExpanded sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Typography variant="h6" sx={{ flexGrow: 1, textTransform: 'capitalize' }}>
                      {environment} Environment
                    </Typography>
                    <Chip
                      label={`${healthyCount}/${totalCount} Healthy`}
                      color={healthyCount === totalCount ? 'success' : healthyCount > 0 ? 'warning' : 'error'}
                      size="small"
                      sx={{ mr: 2 }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                    {apis.map((api, index) => (
                      <Card key={index}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            {getDetailedStatusIcon(api.status)}
                            <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
                              {api.name}
                            </Typography>
                            <Chip
                              label={api.status}
                              color={getStatusColor(api.status) as any}
                              size="small"
                            />
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {api.url}
                          </Typography>
                          
                          {api.error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                              {api.error}
                            </Alert>
                          )}
                          
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableBody>
                                <TableRow>
                                  <TableCell component="th" scope="row">Response Time</TableCell>
                                  <TableCell>{formatResponseTime(api.responseTime)}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell component="th" scope="row">Last Checked</TableCell>
                                  <TableCell>{formatLastChecked(api.lastChecked)}</TableCell>
                                </TableRow>
                                {api.details && Object.keys(api.details).length > 0 && (
                                  <TableRow>
                                    <TableCell component="th" scope="row">Details</TableCell>
                                    <TableCell>
                                      <pre style={{ fontSize: '0.75rem', margin: 0 }}>
                                        {JSON.stringify(api.details, null, 2)}
                                      </pre>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}

          {allHealthData.size === 0 && !loading && (
            <Alert severity="info">
              No health data available. Click refresh to start monitoring.
            </Alert>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default SystemHealth; 